import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // user object | false | null(loading)
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("rc_token");
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("rc_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      (async () => {
        try {
          const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
          localStorage.setItem("rc_token", data.token);
          setUser(data.user);
          window.history.replaceState(null, "", window.location.pathname);
        } catch (e) {
          localStorage.removeItem("rc_token");
          setUser(false);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }
    loadMe();
  }, [loadMe]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("rc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const lang = localStorage.getItem("rc_lang") || "en";
    const { data } = await api.post("/auth/register", { ...payload, lang });
    localStorage.setItem("rc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // Registers the account but does NOT start a session yet — used when email must be
  // verified first. Returns { token, user }; the caller finalizes with finalizeSession().
  const registerPending = async (payload) => {
    const lang = localStorage.getItem("rc_lang") || "en";
    const { data } = await api.post("/auth/register", { ...payload, lang });
    return data;
  };

  const finalizeSession = (token, u) => {
    localStorage.setItem("rc_token", token);
    setUser(u);
  };

  const logout = async () => {
    try { const { disableWebPush } = await import("@/lib/push"); await disableWebPush(); } catch (e) {}
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("rc_token");
    setUser(false);
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, registerPending, finalizeSession, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export { formatApiError };
